import { fillPlaceholders, nextChargeDate, type TriageItem } from "@paredown/shared";
import { useState } from "react";
import { money } from "../api.js";

const ROUTE_LABEL = {
  web_toggle: "Cancel online",
  billing_platform: "Cancel through the billing platform",
  email: "Email them",
  phone: "Call them",
  card_block: "Block it at the card issuer",
} as const;

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="small-button"
      onClick={() =>
        void copy(text).then((ok) => {
          setDone(ok);
          if (ok) setTimeout(() => setDone(false), 1500);
        })
      }
    >
      {done ? "Copied" : label}
    </button>
  );
}

/** The cancellation card for one charge marked Cancel. */
export function CardView({
  item,
  accountEmail,
  onRegenerate,
}: {
  item: TriageItem;
  accountEmail?: string;
  onRegenerate: () => void;
}) {
  const rec = item.card;
  const due = nextChargeDate(item);
  if (!rec || rec.status === "pending") {
    return (
      <div className="card pending" data-testid="cancel-card">
        <p>Looking up the easiest way to cancel… this takes a minute or two.</p>
      </div>
    );
  }
  if (rec.status === "failed" || !rec.card) {
    return (
      <div className="card" data-testid="cancel-card">
        <p className="error">Could not write this card: {rec.error ?? "unknown error"}</p>
        <button type="button" className="small-button" onClick={onRegenerate}>
          Try again
        </button>
      </div>
    );
  }
  const c = rec.card;
  const last = item.charges[item.charges.length - 1];
  const fill = (t: string) =>
    fillPlaceholders(t, {
      lastChargeDate: last?.date ?? item.lastSeen,
      lastChargeAmount: money(last?.amountCents ?? item.lastAmountCents),
      accountEmail,
    });
  const brokenLink = rec.linkCheck && !rec.linkCheck.ok;

  return (
    <div className="card" data-testid="cancel-card">
      <p className="card-head">
        <strong>{ROUTE_LABEL[c.route]}</strong> · saves {money(item.yearlyCostCents)}/yr
        {due && !item.flags.includes("stopped") ? ` · next charge around ${due}` : ""}
      </p>
      {c.url && (
        <p>
          <a href={c.url} target="_blank" rel="noopener noreferrer" className="go">
            Open the cancel page ↗
          </a>{" "}
          {brokenLink && (
            <span className="error small">
              (this link did not load when checked{rec.linkCheck?.status ? `: HTTP ${rec.linkCheck.status}` : ""})
            </span>
          )}
        </p>
      )}
      <ol>
        {c.steps.map((s, i) => (
          <li key={i}>{fill(s)}</li>
        ))}
      </ol>
      {(c.contactEmail || c.contactPhone) && (
        <p>
          {c.contactEmail && (
            <>
              Email <a href={`mailto:${c.contactEmail}`}>{c.contactEmail}</a> <CopyButton text={c.contactEmail} label="Copy" />{" "}
            </>
          )}
          {c.contactPhone && <>Phone {c.contactPhone}</>}
        </p>
      )}
      {c.emailBody && (
        <div className="email">
          {c.emailSubject && (
            <p>
              <strong>Subject:</strong> {fill(c.emailSubject)} <CopyButton text={fill(c.emailSubject)} label="Copy subject" />
            </p>
          )}
          <pre>{fill(c.emailBody)}</pre>
          <CopyButton text={fill(c.emailBody)} label="Copy message" />
        </div>
      )}
      {c.note && <p className="muted">{c.note}</p>}
      <p className="muted small">
        Confidence: {c.confidence}
        {c.sourceUrl && (
          <>
            {" · "}
            <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer">
              source ↗
            </a>
          </>
        )}
        {" · "}
        <button type="button" className="link small" onClick={onRegenerate}>
          Look it up again
        </button>
      </p>
    </div>
  );
}
